import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Monitor, RefreshCw, CheckCircle2 } from 'lucide-react';

// Kiosk pairing screen for NOC/CCTV wall TVs. Shows a 6-digit code an admin approves from
// their phone (Admin → NOC Screens → Approve a screen). Once approved, the TV receives its
// token, stores it and enters the wall — no Turnstile / no typing on the TV.
export default function NocPair() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const to = params.get('to') || '/noc/cameras';
  const [code, setCode] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | waiting | approved | expired | error
  const idRef = useRef(null);
  const pollRef = useRef(null);

  const start = async () => {
    setStatus('loading');
    try {
      const { data } = await api.post('/noc/pair/start');
      idRef.current = data.id;
      setCode(data.code);
      setStatus('waiting');
    } catch {
      setStatus('error');
    }
  };

  useEffect(() => { start(); return () => clearInterval(pollRef.current); }, []);

  useEffect(() => {
    if (status !== 'waiting' || !idRef.current) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/noc/pair/poll?id=${idRef.current}`);
        if (data.status === 'approved' && data.token) {
          clearInterval(pollRef.current);
          setStatus('approved');
          localStorage.setItem('ptt_token', data.token);
          localStorage.removeItem('ptt_user');
          setTimeout(() => { window.location.assign(to); }, 900);
        } else if (data.status === 'expired') {
          clearInterval(pollRef.current);
          setStatus('expired');
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [status, to]);

  return (
    <div className="noc grain min-h-screen flex items-center justify-center p-6" data-testid="noc-pair">
      <div className="max-w-lg w-full text-center">
        <div className="w-16 h-16 rounded-2xl bg-teal-500/10 flex items-center justify-center mx-auto mb-5">
          <Monitor className="w-8 h-8 text-teal-400" />
        </div>
        <div className="font-display font-black text-3xl mb-2">Pair this screen</div>

        {status === 'loading' && <p className="text-zinc-400">Getting a pairing code…</p>}

        {status === 'waiting' && (
          <>
            <p className="text-zinc-400 mb-6">
              On your phone, sign in as an admin and go to <strong className="text-white">Admin → NOC Screens → Approve a screen</strong>, then enter this code. Once approved this screen opens the NOC / CCTV / VMS wall you launched:
            </p>
            <div className="font-display font-black tracking-[0.3em] text-6xl text-teal-400 bg-white/5 rounded-2xl py-6 mb-4 select-all" data-testid="noc-pair-code">
              {code}
            </div>
            <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin" /> Waiting for approval… stays live once connected.
            </div>
            <p className="text-xs text-zinc-600 mt-3">Code expires in 10 minutes.</p>
          </>
        )}

        {status === 'approved' && (
          <div className="flex flex-col items-center gap-3 text-teal-400" data-testid="noc-pair-approved">
            <CheckCircle2 className="w-12 h-12" />
            <p className="text-lg font-semibold">Approved — opening the wall…</p>
          </div>
        )}

        {(status === 'expired' || status === 'error') && (
          <>
            <p className="text-zinc-400 mb-5">{status === 'expired' ? 'That code expired.' : 'Something went wrong.'}</p>
            <button onClick={start} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-teal-500 text-black font-bold hover:bg-teal-400" data-testid="noc-pair-retry">
              <RefreshCw className="w-4 h-4" /> Get a new code
            </button>
          </>
        )}
      </div>
    </div>
  );
}
